import React, { createContext, useContext, useEffect, useState, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import {
  initGA,
  getGAMeasurementId,
  isRealGAMeasurementId,
  setGAMeasurementId,
  updateGAConsent,
  setGAUserId,
  sendPageView,
  sendAnalyticsEvent,
  GAEventOptions,
} from '../utils/analytics';
import { safeLocalStorage } from '../utils/safeStorage';

const COOKIE_CONSENT_KEY = 'carp_arena_cookie_consent';

export interface GoogleAnalyticsContextValue {
  measurementId: string;
  isConnected: boolean;
  hasConsent: boolean;
  trackEvent: (optionsOrName: GAEventOptions | string, params?: Record<string, unknown>) => void;
  trackPageView: (path: string, title?: string) => void;
  changeMeasurementId: (newId: string) => void;
  setConsent: (granted: boolean) => void;
}

const GoogleAnalyticsContext = createContext<GoogleAnalyticsContextValue | null>(null);

export interface GoogleAnalyticsProviderProps {
  children: ReactNode;
}

export function GoogleAnalyticsProvider({ children }: GoogleAnalyticsProviderProps) {
  const { user } = useAuth();
  const [measurementId, setMeasurementIdState] = useState<string>(getGAMeasurementId());
  const [hasConsent, setHasConsentState] = useState<boolean>(() => {
    try {
      const stored = safeLocalStorage.getItem(COOKIE_CONSENT_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.status === 'accepted';
      }
    } catch {
      // ignore
    }
    return false;
  });

  // 1. Инициализация ReactGA при первичном монтировании
  useEffect(() => {
    initGA();
  }, []);

  // 2. Автоматическая синхронизация user_id с GA4
  useEffect(() => {
    if (user && user.id) {
      setGAUserId(user.id);
    } else {
      setGAUserId(null);
    }
  }, [user]);

  // 3. Функция смены Measurement ID
  const changeMeasurementId = (newId: string) => {
    setGAMeasurementId(newId);
    setMeasurementIdState(getGAMeasurementId());
  };

  // 4. Функция обновления согласия на cookie / GA Consent Mode v2
  const setConsent = (granted: boolean) => {
    setHasConsentState(granted);
    updateGAConsent(granted);
    try {
      safeLocalStorage.setItem(
        COOKIE_CONSENT_KEY,
        JSON.stringify({
          status: granted ? 'accepted' : 'declined',
          timestamp: new Date().toISOString(),
        })
      );
    } catch {
      // ignore
    }
  };

  // 5. Общий контекст
  const contextValue = useMemo<GoogleAnalyticsContextValue>(() => ({
    measurementId,
    isConnected: isRealGAMeasurementId(measurementId),
    hasConsent,
    trackEvent: sendAnalyticsEvent,
    trackPageView: sendPageView,
    changeMeasurementId,
    setConsent,
  }), [measurementId, hasConsent]);

  return (
    <GoogleAnalyticsContext.Provider value={contextValue}>
      {children}
    </GoogleAnalyticsContext.Provider>
  );
}

export function useAnalytics(): GoogleAnalyticsContextValue {
  const context = useContext(GoogleAnalyticsContext);
  if (!context) {
    // Безопасный fallback, если используется вне провайдера
    return {
      measurementId: getGAMeasurementId(),
      isConnected: isRealGAMeasurementId(),
      hasConsent: false,
      trackEvent: sendAnalyticsEvent,
      trackPageView: sendPageView,
      changeMeasurementId: setGAMeasurementId,
      setConsent: updateGAConsent,
    };
  }
  return context;
}

export default GoogleAnalyticsProvider;
